/* Created by Interactive 63 - https://interactive63.com */
/**
 * Builds the editor shell: creates the store and import controller, mounts the panels, and adds
 * the resize handles and the small-window notice. Inspector and timeline are placeholders.
 */
import { createEmptyProject } from '../core/model/defaults';
import { PlaybackController } from './playback';
import { StartScreen } from './dialogs/start-screen';
import { ImportController } from './import-controller';
import { lazyExportDialog, lazyPresetsPanel } from './dialogs/lazy-dialogs';
import { PreviewDialog } from './dialogs/preview-dialog';
import { SceneSettingsDialog } from './dialogs/scene-settings';
import { OffsetPopover } from './dialogs/offset-popover';
import { Timeline } from './panels/timeline/timeline';
import { Inspector } from './panels/inspector/inspector';
import { createInspectorContext } from './panels/inspector/inspector-context';
import { LayersPanel } from './panels/layers';
import { StagePanel } from './panels/stage/stage';
import { Topbar } from './panels/topbar';
import { createHelpMenu } from './panels/help-menu';
import { Onboarding } from './onboarding';
import { ShortcutsDialog } from './dialogs/shortcuts-dialog';
import { attachClipboard } from './clipboard';
import { isTextEntryTarget, registerShortcuts } from './shortcuts';
import { ProjectSession } from './persistence/session';
import { registerFileShortcuts } from './persistence/file-shortcuts';
import { Store } from './store/store';
import { readStorage, writeStorage } from './storage';
import { restoreAppearance } from './appearance';
import { h } from './ui/dom';

interface LayoutSizes {
  layers: number;
  inspector: number;
  timeline: number;
}

const LAYOUT_KEY = 'svg-tempo:layout';
const DEFAULT_LAYOUT: LayoutSizes = { layers: 240, inspector: 300, timeline: 260 };

/** Limits a number to an inclusive range. */
function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/** Applies size limits: layers 160 to 400, inspector 240 to 480, timeline 140 to 60% of height. */
function clampLayout(sizes: LayoutSizes): LayoutSizes {
  return {
    layers: clamp(sizes.layers, 160, 400),
    inspector: clamp(sizes.inspector, 240, 480),
    timeline: clamp(sizes.timeline, 140, Math.max(140, window.innerHeight * 0.6)),
  };
}

/** Loads the saved panel sizes, falling back to defaults for missing or invalid data. */
function loadLayout(): LayoutSizes {
  const raw = readStorage(LAYOUT_KEY);
  if (!raw) return clampLayout(DEFAULT_LAYOUT);
  try {
    const parsed = JSON.parse(raw) as Partial<Record<keyof LayoutSizes, unknown>>;
    const pick = (key: keyof LayoutSizes): number =>
      typeof parsed[key] === 'number' ? parsed[key] : DEFAULT_LAYOUT[key];
    return clampLayout({
      layers: pick('layers'),
      inspector: pick('inspector'),
      timeline: pick('timeline'),
    });
  } catch {
    return clampLayout(DEFAULT_LAYOUT);
  }
}

/** Pushes the panel sizes into the CSS custom properties read by the shell grid. */
function applyLayout(shell: HTMLElement, sizes: LayoutSizes): void {
  shell.style.setProperty('--layers-width', `${sizes.layers}px`);
  shell.style.setProperty('--inspector-width', `${sizes.inspector}px`);
  shell.style.setProperty('--timeline-height', `${sizes.timeline}px`);
}

/**
 * Creates a drag handle that resizes one panel. `getValue` converts the pointer position into a
 * new size for the panel, and the result is clamped and persisted by `onResize`.
 */
function createResizeHandle(
  className: string,
  label: string,
  getValue: (event: PointerEvent) => number,
  onResize: (value: number) => void,
): HTMLElement {
  const handle = h('div', {
    class: ['resize-handle', className],
    role: 'separator',
    'aria-label': label,
  });

  handle.addEventListener('pointerdown', (down) => {
    down.preventDefault();
    handle.setPointerCapture(down.pointerId);
    handle.classList.add('dragging');

    /** Resizes the panel to follow the pointer. */
    const move = (event: PointerEvent): void => {
      onResize(getValue(event));
    };
    /** Ends the drag and removes the temporary listeners. */
    const end = (): void => {
      handle.classList.remove('dragging');
      handle.removeEventListener('pointermove', move);
      handle.removeEventListener('pointerup', end);
      handle.removeEventListener('pointercancel', end);
    };
    handle.addEventListener('pointermove', move);
    handle.addEventListener('pointerup', end);
    handle.addEventListener('pointercancel', end);
  });

  return handle;
}

/** Builds the editor shell inside the given mount element. */
export function createApp(root: HTMLElement): void {
  restoreAppearance();
  const store = new Store(createEmptyProject('Untitled'));
  const playback = new PlaybackController(store);
  const shortcuts = new ShortcutsDialog();
  const onboarding = new Onboarding();
  registerShortcuts(store, playback, () => {
    if (shortcuts.isOpen()) shortcuts.close();
    else shortcuts.open();
  });
  attachClipboard(store, () => playback.currentTime, isTextEntryTarget);

  let sizes = loadLayout();
  const shell = h('div', { class: 'shell' });
  applyLayout(shell, sizes);

  /** Updates one panel size, clamping and persisting the whole layout. */
  const resize = (key: keyof LayoutSizes, value: number): void => {
    sizes = clampLayout({ ...sizes, [key]: value });
    applyLayout(shell, sizes);
    writeStorage(LAYOUT_KEY, JSON.stringify(sizes));
  };

  const session = new ProjectSession(store);
  session.start();
  registerFileShortcuts(session);
  const importer = new ImportController(store, session);
  importer.attach();
  importer.onImported(() => {
    onboarding.startIfNew();
  });
  const scene = new SceneSettingsDialog(store);
  const dialogs = {
    scene,
    preview: new PreviewDialog(store),
    exporter: lazyExportDialog(store, scene),
  };
  document.addEventListener('keydown', (event) => {
    if ((event.ctrlKey || event.metaKey) && !event.shiftKey && event.key.toLowerCase() === 'e') {
      event.preventDefault();
      dialogs.exporter.open();
    }
  });
  new Topbar(store, {
    importer,
    playback,
    session,
    dialogs,
    help: createHelpMenu({ shortcuts, onboarding }),
  }).mount(shell);
  new LayersPanel(store, undefined).mount(shell);
  const stage = new StagePanel(store, undefined).mount(shell);
  playback.attachStage(stage);
  stage.bindEditing(createInspectorContext(store, playback, stage));
  const presets = lazyPresetsPanel(store, { stage, playback });
  const offset = new OffsetPopover(store, stage);
  new Inspector(store, { playback, stage, presets }).mount(shell);
  new Timeline(store, { playback, stage, presets, offset }).mount(shell);
  if (import.meta.env.DEV) {
    void import('./dev-hooks').then((hooks) => {
      hooks.installDevHooks(store, playback);
    });
  }
  // The start screen appears once the last project has been looked for, so a returning visit
  // goes straight to its project without a flash of the welcome card.
  void session.restore().finally(() => {
    new StartScreen(store, { controller: importer, session }).mount(stage.el);
  });

  shell.append(
    createResizeHandle(
      'resize-handle-vertical resize-handle-layers',
      'Resize layers panel',
      (event) => event.clientX,
      (value) => {
        resize('layers', value);
      },
    ),
    createResizeHandle(
      'resize-handle-vertical resize-handle-inspector',
      'Resize inspector panel',
      (event) => window.innerWidth - event.clientX,
      (value) => {
        resize('inspector', value);
      },
    ),
    createResizeHandle(
      'resize-handle-horizontal',
      'Resize timeline panel',
      (event) => window.innerHeight - event.clientY,
      (value) => {
        resize('timeline', value);
      },
    ),
  );

  const tooSmall = h(
    'div',
    { class: 'too-small', role: 'alert' },
    h(
      'p',
      null,
      'SVG Tweener needs a window at least 1024 × 640 pixels wide. Try enlarging the window or zooming out.',
    ),
  );

  root.append(shell, tooSmall);
}
